import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Skeleton } from '@heroui/react';
import type { LedgerSettings } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    bpsToPercentText,
    regimeLabelKey,
    type SummaryFigures,
} from '../../dividendsMeta';

type DividendsSummaryProps = {
    /** `null` while the register is in flight — labels stay, values skeleton. */
    figures: SummaryFigures | null;
    /** Null while settings are still loading. */
    settings: LedgerSettings | null;
};

const Figure: React.FC<{
    label: string;
    /** `null` renders a skeleton in the value's place. */
    value: string | null;
    hint?: string;
    slot: string;
    emphasis?: boolean;
}> = ({ label, value, hint, slot, emphasis = false }) => (
    <div className="flex flex-col gap-1 rounded-xl bg-surface-secondary p-4">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">
            {label}
        </span>
        {value === null ? (
            <Skeleton
                className={
                    emphasis ? 'my-1 h-7 w-32 rounded' : 'my-1 h-6 w-28 rounded'
                }
            />
        ) : (
            <span
                className={
                    emphasis
                        ? 'text-2xl font-semibold tabular-nums text-foreground'
                        : 'text-lg font-semibold tabular-nums text-foreground'
                }
                {...getNodeTestIdProp(`dividends-summary-${slot}`)}
            >
                {value}
            </span>
        )}
        {hint ? <span className="text-xs text-muted">{hint}</span> : null}
    </div>
);

/**
 * The screen's headline figures, and the basis they were computed on.
 *
 * **Distributable profit is the emphasised one** because it is the number every
 * other decision on this screen is gated by, and the one an owner is actually
 * asking about.
 *
 * The regime strip is not decoration: every figure below the calculator depends
 * on which regime is in force, and a user who cannot see that a Virtual Zone rate
 * is being applied has no way to catch a misconfigured entity.
 *
 * While the register loads every label stays put and only the values are
 * skeletoned (the repo's loading-state standard). The regime strip is withheld
 * until settings resolve — a wrong regime shown confidently is worse than none.
 */
export const DividendsSummary: React.FC<DividendsSummaryProps> = ({
    figures,
    settings,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div className="flex flex-col gap-4">
            {settings ? (
                <div
                    className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl bg-surface-secondary px-4 py-3"
                    {...getNodeTestIdProp('dividends-regime-strip')}
                >
                    <span className="flex items-center gap-2 text-sm">
                        <Icon
                            aria-hidden
                            className="text-muted"
                            icon="gravity-ui:shield-check"
                        />
                        <span className="text-muted">
                            {t('dividends.regimeInForce')}
                        </span>
                        <span className="font-semibold text-foreground">
                            {t(regimeLabelKey(settings.regime))}
                        </span>
                    </span>
                    <span className="text-sm text-muted">
                        {t('dividends.ratesInForce', {
                            cit: bpsToPercentText(
                                settings.rates.corporateIncomeTaxBps,
                            ),
                            wht: bpsToPercentText(
                                settings.rates.dividendWithholdingTaxBps,
                            ),
                        })}
                    </span>
                </div>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <Figure
                    label={t('dividends.summary.retainedEarnings')}
                    slot="retained-earnings"
                    value={figures?.retainedEarningsText ?? null}
                />
                <Figure
                    label={t('dividends.summary.resultForPeriod')}
                    slot="result-for-period"
                    value={figures?.resultForPeriodText ?? null}
                />
                <Figure
                    emphasis
                    hint={t('dividends.summary.distributableHint')}
                    label={t('dividends.summary.distributableProfit')}
                    slot="distributable"
                    value={figures?.distributableProfitText ?? null}
                />
                <Figure
                    label={t('dividends.summary.declaredUnpaid')}
                    slot="declared-unpaid"
                    value={figures?.declaredUnpaidText ?? null}
                />
                <Figure
                    label={t('dividends.summary.citPayable')}
                    slot="cit-payable"
                    value={figures?.citPayableText ?? null}
                />
                <Figure
                    label={t('dividends.summary.whtPayable')}
                    slot="wht-payable"
                    value={figures?.whtPayableText ?? null}
                />
            </div>
        </div>
    );
};
